import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { truckApi } from '../services/api';
import { initSocket, onTruckStatus, offTruckStatus, subscribeToTruck } from '../services/socket';
import type { TruckStatus, TruckScheduleDay, TruckLocation } from '../types';

interface TruckContextType {
  status: TruckStatus | null;
  schedule: TruckScheduleDay[];
  location: TruckLocation | null;
  locations: TruckLocation[];
  scheduledToday: boolean;
  scheduledHours: { opening: string; closing: string } | null;
  isLoading: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
}

const TruckContext = createContext<TruckContextType | undefined>(undefined);

export const TruckProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<TruckStatus | null>(null);
  const [schedule, setSchedule] = useState<TruckScheduleDay[]>([]);
  const [location, setLocation] = useState<TruckLocation | null>(null);
  const [locations, setLocations] = useState<TruckLocation[]>([]);
  const [scheduledToday, setScheduledToday] = useState(false);
  const [scheduledHours, setScheduledHours] = useState<{ opening: string; closing: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTruckData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [statusRes, scheduleRes, locationRes] = await Promise.all([
        truckApi.getStatus(),
        truckApi.getSchedule(),
        truckApi.getLocation(),
      ]);

      setStatus(statusRes.status);
      setScheduledToday(statusRes.scheduled_today);
      setScheduledHours(statusRes.scheduled_hours);
      setSchedule(scheduleRes.schedule);
      setLocation(locationRes.location);
      setLocations(locationRes.locations || []);
    } catch (err) {
      console.error('Failed to fetch truck data:', err);
      setError('Impossible de charger les informations du food truck');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTruckData();
  }, [fetchTruckData]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    initSocket();

    subscribeToTruck();

    const handleStatusUpdate = (newStatus: TruckStatus) => {
      setStatus(newStatus);
    };

    onTruckStatus(handleStatusUpdate);

    return () => {
      offTruckStatus(handleStatusUpdate);
    };
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await truckApi.getStatus();
      setStatus(res.status);
      setScheduledToday(res.scheduled_today);
      setScheduledHours(res.scheduled_hours);
    } catch (err) {
      console.error('Failed to refresh truck status:', err);
    }
  }, []);

  const value = useMemo<TruckContextType>(() => ({
    status,
    schedule,
    location,
    locations,
    scheduledToday,
    scheduledHours,
    isLoading,
    error,
    refreshStatus,
  }), [status, schedule, location, locations, scheduledToday, scheduledHours, isLoading, error, refreshStatus]);

  return <TruckContext.Provider value={value}>{children}</TruckContext.Provider>;
};

export const useTruck = (): TruckContextType => {
  const context = useContext(TruckContext);
  if (context === undefined) {
    throw new Error('useTruck must be used within a TruckProvider');
  }
  return context;
};

export default TruckContext;
